/** Schema.org JSON-LD helpers for media detail pages (Google Search rich results). */

export function buildMediaAggregateRating({ tmdbScore, voteCount, reviewCount, reviewAvg }) {
  const votes = Number(voteCount);
  if (tmdbScore != null && Number.isFinite(votes) && votes > 0) {
    return {
      '@type': 'AggregateRating',
      ratingValue: Number(tmdbScore),
      bestRating: 10,
      worstRating: 0,
      ratingCount: Math.round(votes),
    };
  }
  const reviews = Number(reviewCount);
  if (reviews > 0 && reviewAvg != null) {
    return {
      '@type': 'AggregateRating',
      ratingValue: Number(reviewAvg),
      bestRating: 5,
      worstRating: 1,
      reviewCount: Math.round(reviews),
    };
  }
  return undefined;
}

export function buildTrailerVideoObject(video, { pageTitle, description, posterUrl, fallbackDate }) {
  if (!video || (!video.key && !video.url)) return null;
  const key = String(video.key || '').trim();
  const thumb = key ? `https://img.youtube.com/vi/${key}/hqdefault.jpg` : posterUrl || undefined;
  const uploadDate =
    video.publishedAt ||
    (fallbackDate && String(fallbackDate).length >= 10 ? String(fallbackDate).slice(0, 10) : undefined);
  const desc = String(description || video.name || pageTitle || '').trim();

  return {
    '@type': 'VideoObject',
    name: video.name || pageTitle || 'Trailer',
    description: desc || undefined,
    uploadDate: uploadDate || undefined,
    thumbnailUrl: thumb,
    embedUrl: key ? `https://www.youtube.com/embed/${key}` : undefined,
    url: video.url || (key ? `https://www.youtube.com/watch?v=${key}` : undefined),
  };
}

export function buildMediaDetailJsonLd({
  category,
  pageTitle,
  pageDescription,
  pageImage,
  hero,
  region,
  tmdbScore,
  reviewCount,
  reviewAvg,
  runtimeMinutes,
}) {
  const isMovie = category === 'movie' || category === 'anime_movie';
  const trailerSource = (hero?.videos?.trailers || [])[0];
  const trailer = buildTrailerVideoObject(trailerSource, {
    pageTitle,
    description: pageDescription,
    posterUrl: pageImage,
    fallbackDate: hero?.releaseDate,
  });

  return {
    '@context': 'https://schema.org',
    '@type': isMovie ? 'Movie' : 'TVSeries',
    name: pageTitle,
    description: pageDescription,
    image: pageImage || undefined,
    datePublished: hero?.releaseDate || undefined,
    aggregateRating: buildMediaAggregateRating({
      tmdbScore,
      voteCount: hero?.voteCount,
      reviewCount,
      reviewAvg,
    }),
    contentRating: hero?.certification || undefined,
    duration: runtimeMinutes ? `PT${runtimeMinutes}M` : undefined,
    actor: (hero?.credits?.cast || []).slice(0, 8).map((c) => ({
      '@type': 'Person',
      name: c.name,
    })),
    director: (hero?.credits?.crew || [])
      .filter((c) => c.job === 'Director' || c.job === 'Creator')
      .slice(0, 3)
      .map((c) => ({ '@type': 'Person', name: c.name })),
    trailer: trailer ? [trailer] : undefined,
    inLanguage: region || 'en',
  };
}
